import React from 'react';
import { getSpotlightLabel } from '../../lib/badges';
import { HeartIcon } from './icons';
import { FOCUS_RING } from './tokens';

/**
 * HOME-01 — the redesign's badge set, in one file so a badge cannot be
 * re-declared with a near-miss colour on the next card someone builds.
 *
 * Each one is a statement about the listing, and each statement has an owner:
 *   Spotlight  — the agent paid KES 2,000 for 7 days of carousel placement.
 *   Verified   — the agent completed verification. Optional, never a gate.
 *   Fresh      — derived from the listing's own created_at, nothing else.
 * Nothing here infers a claim from adjacent data.
 */

/**
 * The paid-boost mark. Says who boosted and for how long via the row's tooltip,
 * never "featured" or "top" — Spotlight buys placement in this carousel and
 * nothing in the organic feed's ranking.
 */
export const SpotlightBadge: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span
    className={`inline-flex items-center rounded-md border border-[#EAD9AC] bg-[#FBF2DE] px-2 py-[3px] text-[11px] font-bold text-[#7A5A0E] ${className}`}
  >
    ★ {getSpotlightLabel()}
  </span>
);

/**
 * Verification is optional and badge-based: an agent can list without it, so
 * the absence of this badge means "not verified", never "not checked yet" and
 * never anything about the listing's legitimacy.
 *
 * Renders nothing while `verified` is false, which is every row today — no
 * listing query joins agents_public.is_verified (HOME-04).
 */
export const VerifiedAgentBadge: React.FC<{ verified: boolean; className?: string }> = ({
  verified,
  className = '',
}) => {
  if (!verified) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md bg-[#EBF0EB] px-2 py-[2.5px] text-[11px] font-bold text-[#134025] ${className}`}
    >
      ✓ Verified agent
    </span>
  );
};

/**
 * The New this week tag: "Listed today" on a marketplace card, "Added today"
 * on an indexed one (freshnessTag). Caller decides whether it has one.
 */
export const FreshnessBadge: React.FC<{ label: string }> = ({ label }) => (
  <span className="pointer-events-none absolute left-2.5 top-2.5 rounded-md bg-[#134025] px-[9px] py-[3px] text-[11px] font-bold text-white">
    {label}
  </span>
);

/** Gallery size chip. Hidden when the row's photo count is unknown. */
export const PhotoCountBadge: React.FC<{ count: number | null; className?: string }> = ({
  count,
  className = '',
}) => {
  if (!count || count < 1) return null;
  return (
    <span
      className={`pointer-events-none absolute rounded-[5px] bg-black/55 px-2 py-0.5 text-[11px] font-semibold text-white ${className}`}
    >
      1/{count}
    </span>
  );
};

/** An amenity we display because the listing carries the flag. Never inferred. */
export const AmenityTag: React.FC<{ label: string; icon?: React.ReactNode }> = ({
  label,
  icon,
}) => (
  <span className="inline-flex items-center gap-[5px] rounded-md bg-[#F1F0EA] px-[9px] py-[3.5px] text-[11px] font-semibold text-[#4A4A45]">
    {icon}
    {label}
  </span>
);

/**
 * The save control.
 *
 * The 44×44 hit area is the button; the 36px white circle is only what you see.
 * The design specifies that split and it is the reason the control is reliably
 * tappable — shrinking the button to the circle would meet the design and fail
 * the thumb, so the two sizes are separate on purpose.
 *
 * It sits inside the card's image region, which is `position: relative` with
 * `z-index: auto` — deliberately NOT a stacking context, so this button's
 * `z-[2]` is compared directly against the stretched card link's `z-[1]` and
 * wins. Giving that region a z-index of its own would trap the heart inside it
 * and is how a save control ends up looking pressable while silently
 * navigating to the listing.
 *
 * Signed out, `onSave` routes to sign-in rather than doing nothing: an
 * unauthenticated tap on a heart is intent, and dropping it silently is the
 * worst of the three options.
 */
export const SaveHeartButton: React.FC<{
  saved: boolean;
  onSave: (e: React.MouseEvent) => void;
  /**
   * The listing this heart belongs to.
   *
   * A results page holds twenty of these, and "Save home" twenty times is a
   * screen-reader user being offered twenty identical controls with no way to
   * tell which home each one saves. The accessible name carries the title, so
   * the rotor reads "Save 3 Bed Apartment, Kilimani" — which is the whole point
   * of an accessible name on an icon-only control.
   */
  listingTitle?: string;
  className?: string;
  /**
   * Visual circle diameter. The hit area stays 44×44 regardless.
   *
   * 36/19 rather than the design's 30/15, on Dirie's instruction after seeing
   * it on a phone: at 30px over a bright listing photograph the control was
   * there but not findable, which on a save control means the intent is simply
   * lost. This is a deliberate, PO-approved departure from the drawing — the
   * only one on this card.
   */
  circle?: number;
  iconSize?: number;
}> = ({ saved, onSave, listingTitle, className = '', circle = 36, iconSize = 19 }) => (
  <button
    type="button"
    onClick={onSave}
    aria-label={
      listingTitle
        ? `${saved ? 'Remove from saved' : 'Save'} ${listingTitle}`
        : saved
          ? 'Remove from saved'
          : 'Save home'
    }
    aria-pressed={saved}
    className={`group absolute flex h-11 w-11 items-center justify-center rounded-full border-none bg-transparent p-0 ${FOCUS_RING} ${className}`}
  >
    {/*
      Fully opaque white, not white/92: the tile has to be a solid ground for
      the glyph, and 8% of a bright sky bleeding through was part of why the
      heart vanished. The drop shadow is what separates the circle from a photo
      of similar lightness — without it, white-on-white leaves only the glyph.

      `active:scale-90` is the tap acknowledgement. A save that gives no
      feedback until the network answers reads as a control that did nothing.
    */}
    <span
      className="flex items-center justify-center rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,.28)] transition-transform duration-100 group-active:scale-90 group-hover:scale-105"
      style={{ width: circle, height: circle }}
    >
      <HeartIcon size={iconSize} saved={saved} />
    </span>
  </button>
);
